// Property archetype builders for WORLD COSTA BLANCA.
//
// SOURCE: docs/32-VISUAL-SEMANTIC-META-TOKENS.md — recipes A-E and the
//   "5-8 visibly different properties from shared systems" goal.
// METHOD: IMPLEMENT — five builders keyed by ARCH.FAMILY; every builder
//   honors the shared P0 tokens (MATERIAL.FACADE/TRIM/ROOF, ARCH.ROOF,
//   ARCH.OPENINGS, LANDSCAPE.STYLE, WATER.TYPE) so variation is systemic,
//   not hand-authored per building (ANTI.clone_house_repetition).
// CONTRACT: each build* returns { group, footprint: { w, d }, height }.
//   The caller positions the group, registers colliders, and stores anchors.
//   A small beacon sphere child is included — main.js animates it.

import * as THREE from 'three';
import { FACADES, TRIMS, ROOF_MATERIALS, GLASS, WATER_SURFACE } from '../tokens/catalog.js';
import { limewashTexture } from './textures.js';
import { cel } from '../core/toon.js';

// Cel by default (Sorolla colored shadows). cache:false because every
// building jitters its own color — sharing cached instances would leak tints.
function std(params) {
  return cel({ color: params.color, bands: params.bands ?? 3, cache: false });
}

// Seeded per-building tint jitter — kills flat repetition without breaking
// the token palette (ANTI.clone_house_repetition / ANTI.flat_white_surfaces).
function jitter(material, rng, amount = .035) {
  if (rng) material.color.offsetHSL((rng() - .5) * .012, (rng() - .5) * .02, (rng() - .5) * amount);
  return material;
}

function box(w, h, d, material, x = 0, y = 0, z = 0) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
  mesh.position.set(x, y, z);
  mesh.castShadow = mesh.receiveShadow = true;
  return mesh;
}

function beacon(material) {
  const b = new THREE.Mesh(
    new THREE.SphereGeometry(.16, 10, 8),
    new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: material.color, emissiveIntensity: 1.2 })
  );
  b.userData.beacon = true; // main.js animates exactly this child
  return b;
}

function gableRoof(w, d, rise, material, y) {
  // Explicit triangular prism: ridge along z, eaves at x = ±w/2.
  // (A rotated 3-sided cylinder proved orientation-fragile — QA coast shot.)
  const hw = w / 2, hd = d / 2;
  const v = [
    // south triangle
    -hw, 0, hd,  hw, 0, hd,  0, rise, hd,
    // north triangle
    hw, 0, -hd,  -hw, 0, -hd,  0, rise, -hd,
    // west slope
    -hw, 0, hd,  0, rise, hd,  0, rise, -hd,
    -hw, 0, hd,  0, rise, -hd,  -hw, 0, -hd,
    // east slope
    hw, 0, hd,  hw, 0, -hd,  0, rise, -hd,
    hw, 0, hd,  0, rise, -hd,  0, rise, hd
  ];
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(v, 3));
  geo.computeVertexNormals();
  const roof = new THREE.Mesh(geo, material.clone());
  // Material.clone() drops/serialises the shadow-tint patch — restore it.
  if (material.userData?.shadowTint) roof.material.userData.shadowTint = material.userData.shadowTint;
  if (material.onBeforeCompile) roof.material.onBeforeCompile = material.onBeforeCompile;
  if (material.customProgramCacheKey) roof.material.customProgramCacheKey = material.customProgramCacheKey;
  roof.material.side = THREE.DoubleSide;
  roof.position.set(0, y, 0);
  roof.castShadow = roof.receiveShadow = true;
  return roof;
}

function shutteredWindow(trim, glass, x, y, z, { w = .3, h = .42, shutters = true } = {}) {
  const g = new THREE.Group();
  g.add(box(w, h, .05, glass, x, y, z));
  if (shutters) {
    g.add(box(.09, h, .045, trim, x - w / 2 - .06, y, z));
    g.add(box(.09, h, .045, trim, x + w / 2 + .06, y, z));
  }
  return g;
}

function glazingBand(glass, w, h, x, y, z) {
  return box(w, h, .06, glass, x, y, z);
}

function mats(T, rng) {
  const facadeDef = FACADES[T['MATERIAL.FACADE']] || FACADES['lime-white'];
  // high-key ramp keeps whitewash luminous in shade (Sorolla whites)
  const facade = jitter(std({ ...facadeDef, bands: 'soft3' }), rng);
  // limewash texture reads on traditional facades; crisp modern stays clean
  if (T['MATERIAL.FACADE'] !== 'crisp-white') facade.map = limewashTexture();
  return {
    facade,
    trim: jitter(std(TRIMS[T['MATERIAL.TRIM']] || TRIMS['altea-blue']), rng, .02),
    roof: jitter(std(ROOF_MATERIALS[T['MATERIAL.ROOF']] || ROOF_MATERIALS['terracotta']), rng, .03),
    glass: std(GLASS),
    water: std(WATER_SURFACE),
    stone: std({ color: 0xcfc6b2, roughness: 1 }),
    green: std({ color: 0x5f7d5a, roughness: .95 }),
    iron: std({ color: 0x2b2f33, roughness: .55, metalness: .35 })
  };
}

// --- Recipe A: whitewashed old-town house --------------------------------
function buildOldtownHouse(T, rng) {
  const M = mats(T, rng);
  const g = new THREE.Group();
  const w = 2.1 + rng() * .3, d = 1.9 + rng() * .2, h = 2.5 + rng() * .3;
  g.add(box(w, h, d, M.facade, 0, h / 2, 0));
  // flat-parapet roof
  g.add(box(w + .12, .1, d + .12, M.roof.color ? M.roof : M.stone, 0, h + .05, 0));
  g.add(box(w + .12, .22, .08, M.facade, 0, h + .16, d / 2 + .02));
  g.add(box(w + .12, .22, .08, M.facade, 0, h + .16, -d / 2 - .02));
  // deep-small openings, shuttered, trim-colored door
  for (const fy of [.75, 1.65]) {
    g.add(shutteredWindow(M.trim, M.glass, -w * .22, fy, d / 2 + .02));
    g.add(shutteredWindow(M.trim, M.glass, w * .28, fy, d / 2 + .02));
  }
  // wrought-iron balcony on the upper floor (Altea micro-DNA)
  g.add(box(.9, .05, .3, M.iron, 0, 1.42, d / 2 + .16));
  for (let i = -2; i <= 2; i += 1) g.add(box(.03, .34, .03, M.iron, i * .2, 1.62, d / 2 + .3));
  g.add(box(.94, .04, .04, M.iron, 0, 1.8, d / 2 + .3));
  // blue ceramic plaque beside the door
  g.add(box(.16, .22, .04, std({ color: 0x2e6f9e, roughness: .35 }), -.45, 1.15, d / 2 + .03));
  g.add(box(.5, .95, .07, M.trim, 0, .48, d / 2 + .02));
  g.add(box(.7, .08, .3, M.stone, 0, .04, d / 2 + .16)); // entry step
  const b = beacon(M.trim); b.position.set(w / 2, h + .45, d / 2); g.add(b);
  return { group: g, footprint: { w, d }, height: h };
}

// --- Recipe B: modern sea villa -------------------------------------------
function buildSeaVilla(T, rng) {
  const M = mats(T, rng);
  const g = new THREE.Group();
  // cube-stack massing
  g.add(box(3.3, 1.5, 2.6, M.facade, 0, .75, 0));
  g.add(box(2.3, 1.35, 2.0, M.facade, -.35, 1.5 + .675, -.2));
  // full-glazing bands facing +z (the sea side)
  g.add(glazingBand(M.glass, 2.9, 1.0, 0, .8, 1.31));
  g.add(glazingBand(M.glass, 1.9, .9, -.35, 2.1, .81));
  // terrace roof slab + slim pergola
  g.add(box(2.5, .08, 2.2, M.stone, -.35, 2.9, -.2));
  for (let i = 0; i < 4; i += 1) g.add(box(2.5, .05, .07, M.trim, -.35, 3.15, -1.0 + i * .55));
  // infinity-edge pool in front
  if (T['WATER.TYPE'] === 'infinity-edge') {
    g.add(box(2.6, .3, 1.5, M.facade, .2, .15, 2.3));
    g.add(box(2.5, .06, 1.4, M.water, .2, .32, 2.3));
  }
  const b = beacon(M.trim); b.position.set(1.65, 1.9, 1.3); g.add(b);
  return { group: g, footprint: { w: 3.3, d: 3.1 }, height: 2.95 };
}

// --- Recipe C: mediterranean family villa ---------------------------------
function buildFamilyVilla(T, rng) {
  const M = mats(T, rng);
  const g = new THREE.Group();
  const w = 3.4, d = 2.4, h = 2.2;
  g.add(box(w, h, d, M.facade, 0, h / 2, 0));
  g.add(gableRoof(w + .2, d + .25, .55, M.roof, h));
  // arched door: box + half cylinder
  g.add(box(.55, .8, .07, M.trim, 0, .4, d / 2 + .02));
  const arch = new THREE.Mesh(new THREE.CylinderGeometry(.28, .28, .07, 12, 1, false, 0, Math.PI), M.trim);
  arch.rotation.x = Math.PI / 2; arch.rotation.z = Math.PI / 2;
  arch.position.set(0, .8, d / 2 + .02);
  g.add(arch);
  g.add(shutteredWindow(M.trim, M.glass, -w * .28, .85, d / 2 + .02));
  g.add(shutteredWindow(M.trim, M.glass, w * .28, .85, d / 2 + .02));
  g.add(shutteredWindow(M.trim, M.glass, -w * .28, 1.65, d / 2 + .02, { w: .26, h: .36 }));
  g.add(shutteredWindow(M.trim, M.glass, w * .28, 1.65, d / 2 + .02, { w: .26, h: .36 }));
  // med-garden: tree + pots
  if ((T['LANDSCAPE.STYLE'] || 'med-garden') !== 'none') {
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(.07, .1, .9, 8), M.trim);
    trunk.position.set(-w / 2 - .6, .45, .6); g.add(trunk);
    const crown = new THREE.Mesh(new THREE.SphereGeometry(.5, 9, 7), M.green);
    crown.position.set(-w / 2 - .6, 1.15, .6); g.add(crown);
    g.add(box(.22, .18, .22, M.roof, w / 2 + .35, .09, .7));
    g.add(box(.22, .18, .22, M.roof, w / 2 + .35, .09, 1.05));
  }
  const b = beacon(M.trim); b.position.set(w / 2, h + .5, d / 2); g.add(b);
  return { group: g, footprint: { w, d }, height: h + .55 };
}

// --- Recipe D: hillside apartment (terraced) ------------------------------
function buildHillsideApartment(T, rng) {
  const M = mats(T, rng);
  const g = new THREE.Group();
  const levels = [
    { w: 3.2, d: 2.6, h: 1.35 },
    { w: 2.8, d: 2.2, h: 1.3 },
    { w: 2.4, d: 1.8, h: 1.25 }
  ];
  let y = 0;
  levels.forEach((lv, i) => {
    const zBack = -i * .25;
    g.add(box(lv.w, lv.h, lv.d, M.facade, 0, y + lv.h / 2, zBack));
    // balcony grid: slab + rail on the front of each level
    g.add(box(lv.w * .8, .07, .5, M.stone, 0, y + lv.h + .035, zBack + lv.d / 2 + .25));
    g.add(box(lv.w * .8, .3, .05, M.trim, 0, y + lv.h + .22, zBack + lv.d / 2 + .48));
    g.add(glazingBand(M.glass, lv.w * .6, lv.h * .55, 0, y + lv.h * .55, zBack + lv.d / 2 + .02));
    y += lv.h;
  });
  // low-slope roof
  const roof = box(levels[2].w + .15, .07, levels[2].d + .15, M.roof, 0, y + .035, -.5);
  roof.rotation.x = .04;
  g.add(roof);
  const b = beacon(M.trim); b.position.set(levels[0].w / 2, 1.7, levels[0].d / 2); g.add(b);
  return { group: g, footprint: { w: 3.2, d: 3.1 }, height: y };
}

// --- Recipe E: townhouse with patio ---------------------------------------
function buildTownhouse(T, rng) {
  const M = mats(T, rng);
  const g = new THREE.Group();
  const w = 1.9, d = 2.2, h = 3.5;
  g.add(box(w, h, d, M.facade, 0, h / 2, 0));
  g.add(gableRoof(w + .15, d + .2, .4, M.roof, h));
  for (const fy of [1.5, 2.5]) {
    g.add(shutteredWindow(M.trim, M.glass, 0, fy, d / 2 + .02, { w: .34, h: .5 }));
  }
  g.add(box(.5, .95, .07, M.trim, -.35, .48, d / 2 + .02));
  g.add(shutteredWindow(M.trim, M.glass, .45, .7, d / 2 + .02, { w: .3, h: .4 }));
  // patio wall in front
  g.add(box(w + .8, .55, .08, M.facade, 0, .275, d / 2 + 1.0));
  g.add(box(.08, .55, 1.0, M.facade, -(w + .8) / 2, .275, d / 2 + .5));
  const b = beacon(M.trim); b.position.set(w / 2, h + .35, d / 2); g.add(b);
  return { group: g, footprint: { w: w + .8, d: d + 1.1 }, height: h + .4 };
}

const BUILDERS = {
  'oldtown-house': buildOldtownHouse,
  'sea-villa': buildSeaVilla,
  'family-villa': buildFamilyVilla,
  'hillside-apartment': buildHillsideApartment,
  'townhouse': buildTownhouse
};

/** Dispatch on ARCH.FAMILY; unknown families fall back to oldtown-house. */
export function buildPropertyArchetype(tokens, rng) {
  const family = tokens['ARCH.FAMILY'] || 'oldtown-house';
  return (BUILDERS[family] || buildOldtownHouse)(tokens, rng);
}
